<template>
  <div class="space-y-6">
    <!-- Header Section -->
    <div class="bg-white shadow-md rounded-lg p-6">
      <div class="flex flex-col md:flex-row md:items-center md:justify-between">
        <div>
          <h1 class="text-3xl font-bold text-gray-900">Jouw Tags</h1>
          <p class="text-gray-600 mt-2">
            Beheer en bekijk al je QR-code tags
            <span class="text-gray-400">({{ tags.length }} tag{{ tags.length === 1 ? '' : 's' }})</span>
          </p>
        </div>
        <router-link :to="{ name: 'Tags/new' }" class="mt-4 md:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-semibold text-sm transition">
          <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clip-rule="evenodd"></path>
          </svg>
          Nieuwe tag maken
        </router-link>
      </div>
    </div>

    <!-- Statistics Summary -->
    <div class="grid grid-cols-1 md:grid-cols-3 gap-4">
      <div class="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p class="text-sm font-medium text-blue-600 uppercase">Totaal Tags</p>
        <div v-if="loading" class="flex items-center mt-2">
          <div class="animate-spin rounded-full h-8 w-8 border-4 border-blue-200 border-t-blue-600"></div>
        </div>
        <p v-else class="text-2xl font-bold text-blue-900">{{ tags.length }}</p>
      </div>
      <div class="bg-green-50 border border-green-200 rounded-lg p-4">
        <p class="text-sm font-medium text-green-600 uppercase">Totaal Triggers</p>
        <div v-if="loading" class="flex items-center mt-2">
          <div class="animate-spin rounded-full h-8 w-8 border-4 border-green-200 border-t-green-600"></div>
        </div>
        <p v-else class="text-2xl font-bold text-green-900">{{ totalTriggers }}</p>
      </div>
      <div class="bg-purple-50 border border-purple-200 rounded-lg p-4">
        <p class="text-sm font-medium text-purple-600 uppercase">Gemiddeld/Tag</p>
        <div v-if="loading" class="flex items-center mt-2">
          <div class="animate-spin rounded-full h-8 w-8 border-4 border-purple-200 border-t-purple-600"></div>
        </div>
        <p v-else class="text-2xl font-bold text-purple-900">{{ averageTriggers }}</p>
      </div>
    </div>

    <!-- Tags List -->
    <div class="bg-white shadow-md rounded-lg p-6">
      <div v-if="loading" class="text-center py-8 text-gray-500">
        Tags laden...
      </div>

      <div v-else-if="tags.length === 0" class="text-center py-12">
        <svg class="mx-auto h-16 w-16 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z"></path>
        </svg>
        <h3 class="mt-4 text-lg font-medium text-gray-900">Geen tags gevonden</h3>
        <p class="mt-2 text-gray-500">Begin met het aanmaken van je eerste QR-code tag.</p>
        <router-link :to="{ name: 'Tags/new' }" class="mt-6 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-semibold text-sm transition">
          Maak je eerste tag aan
        </router-link>
      </div>

      <ul v-else class="space-y-4">
        <li v-for="tag in tags" :key="tag.id">
          <div class="flex flex-col md:flex-row md:items-center p-5 rounded-xl bg-gray-50 hover:bg-gray-100 transition border border-gray-200">
            <div class="flex-shrink-0 mr-5">
              <img class="w-24 h-24 rounded-lg shadow-sm" :src="`data:image/png;base64,${tag.qr_code}`" alt="QR Code" />
            </div>

            <div class="flex-1 min-w-0 mt-4 md:mt-0">
              <h3 class="text-xl font-semibold text-gray-900 truncate">
                {{ tag.name }}
              </h3>
              <div class="mt-2 space-y-1">
                <p class="text-sm text-gray-700">
                  <span class="font-medium">Short URL:</span>
                  <a :href="tag.url" target="_blank" class="text-blue-600 hover:text-blue-800 hover:underline ml-1">
                    {{ tag.url }}
                  </a>
                </p>
                <p class="text-sm text-gray-600">
                  <span class="font-medium">Target:</span>
                  <span class="ml-1">{{ tag.target_url }}</span>
                </p>
              </div>
              <div class="mt-3 flex flex-wrap gap-2">
                <button
                  @click="downloadQRCode(tag)"
                  class="inline-flex items-center text-sm text-green-600 font-semibold hover:text-green-800 hover:underline"
                >
                  <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zm3.293-7.707a1 1 0 011.414 0L9 10.586V3a1 1 0 112 0v7.586l1.293-1.293a1 1 0 111.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd"></path>
                  </svg>
                  Download QR
                </button>

                <router-link
                  :to="{ name: 'Tags/edit', params: { id: tag.id } }"
                  class="inline-flex items-center text-sm text-gray-700 font-semibold hover:text-gray-900 hover:underline"
                >
                  <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M13.586 3.586a2 2 0 112.828 2.828l-.793.793-2.828-2.828.793-.793zM11.379 5.793L3 14.172V17h2.828l8.38-8.379-2.83-2.828z"></path>
                  </svg>
                  Bewerken
                </router-link>
              </div>
            </div>

            <div class="flex items-center mt-4 md:mt-0 md:ml-6">
              <div class="text-center px-6 py-4 bg-white rounded-lg shadow-md border border-gray-200">
                <p class="text-3xl font-bold text-gray-900">{{ tag.events_count }}</p>
                <p class="text-xs text-gray-600 uppercase font-medium mt-1">Triggers</p>
              </div>
            </div>
          </div>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import {mapStores} from "pinia";
import {useTagsStore} from "@/stores/tags.js";
import { toast } from 'vue3-toastify';

export default {
  data() {
    return {
    }
  },
  computed: {
    ...mapStores(useTagsStore),
    tags() {
      return this.tagsStore.allTags
    },
    loading() {
      return this.tagsStore.isLoading
    },
    totalTriggers() {
      return this.tagsStore.totalTriggers
    },
    averageTriggers() {
      return this.tagsStore.averageTriggers
    }
  },
  watch: {
    $route(to, from) {
      // Refetch tags when navigating to this page
      if (to.name === 'Tags') {
        this.tagsStore.fetchTags();
      }
    }
  },
  mounted() {
    this.tagsStore.fetchTags();
  },
  activated() {
    this.tagsStore.fetchTags();
  },
  methods: {
    downloadQRCode(tag) {
      if (!tag.qr_code) return;

      const fileName = `${tag.name.replace(/[^a-z0-9]/gi, '_')}_qr.png`;
      const link = document.createElement('a');
      link.href = 'data:image/png;base64,' + tag.qr_code;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`${tag.name} QR code gedownload!`, {
        position: toast.POSITION.BOTTOM_RIGHT,
      });
    }
  }
}
</script>


